import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { Subscription } from 'rxjs';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import * as Objects from '@core/objects';
import { ProjectsService } from "@app/services/app/projects.service";
import { VersionsService } from "@app/services/app/versions.service";
import { AddVersion, AddVersionErrors } from "@core/models/add-version";
import { FormBuilder, FormGroup } from "@angular/forms";
import { AddVersionService } from "@app/services/app/add-version.service";

@Component({
    selector: 'versions-add-main',
    templateUrl: './main.component.html',
    styleUrls: ['./main.component.scss']
})
export class VersionsAddMainComponent implements OnInit, OnDestroy {
    
    public readonly STATUS_LOADING = 0;
    public readonly STATUS_DONE = 1;
    public readonly STATUS_ERROR = 2;


    @Input() project: Objects.Request;
    @Input() labels: Array<Objects.Label>;
    @Input() editTagsAccess:boolean = false;
    @Input() versionNumber: string = "1.0";

    // for view reminder    
    public readonly VIEW_MODAL_NONE = 0; 
    public readonly VIEW_MODAL_UPLOAD_ERROR = 1;   

    public status: number = this.STATUS_LOADING;
    public modalView: number = this.VIEW_MODAL_NONE;
    public modalUploadErrorText: string = "You can not upload this artwork to this project. Please try again.<br/> If the error persists, please reach out to our support team through the Help Me button.";
    public creatingNewVersion: boolean = false;
    public versionSaved: boolean = false;

    public errorModal: boolean = false;   // For validation errors (due_date, info, etc.)
    public forbiddenErrorModal: boolean = false; // For forbidden & unauthorized
    public generalErrorModal: boolean = false;   // For other errors

    public files: Array<Objects.FileMetadata> = [];
    public form: FormGroup = null;
    public versionLabels: Array<Objects.Label> = [];
    public availableTagGroups: Array<Objects.Label[]>;
    protected relatedVersionsGroups: Array<Objects.RelatedVersions> = [];

    public allVersionStatus: Array<Objects.Status> = null;

    public errors: AddVersionErrors = {
        files: false,
        labels: false,
        version: false, // for the version numbers
        status: false,
        forbidden: false,
        unauthorized: false,
        server: false,
        other: false
    };

    protected subscriptions = new Subscription();

    // new version default properties
    public addVersionDataDefault: AddVersion = {
        files: [],
        labels: [],
        version: 1,
        subversion: 0,
        status: null
    }
    public addVersionData: AddVersion;

    constructor(
        private location: Location,
        private router: Router,
        private addVersionSrv: AddVersionService,
        private versionsSrv: VersionsService,
        private projectsSrv: ProjectsService,
        private fb: FormBuilder
    ){}
   
    ngOnInit(): void {
        this.addVersionDataDefault.version = +this.versionNumber.split(".")[0];
        this.addVersionDataDefault.subversion = +this.versionNumber.split(".")[1] + 1;

        this.addVersionData = this.addVersionDataDefault;
        this.form = this.fb.group({});
        this.setLabels(this.labels);
        
        // Load related versions if editTagsAccess is false (no access to edit tags)
        if (!this.editTagsAccess) {
            this.subscriptions.add(
                this.projectsSrv.selectRelatedVersions().subscribe((relatedsList: Array<Objects.RelatedVersions>) => {
                    if (relatedsList == null) {
                        // Related versions not yet loaded, trigger load
                        this.projectsSrv.loadRelatedVersions();
                    } else {
                        // Extract tags from each related version group
                        this.extractAvailableTagGroups(relatedsList);

                        this.initView();
                    }
                })
            );
        }
        
        this.subscriptions.add(
            this.versionsSrv.getAllStatus$().subscribe((status: Array<Objects.Status>) => {
                if(status != null){
                    this.addVersionData.status = +status[0].id;
                }
                this.allVersionStatus = status;
                this.initView();
            })
        );

        this.subscriptions.add(
            this.addVersionSrv.selectCreatingNewVersion().subscribe(creating => {
                this.creatingNewVersion = creating;
                if (creating) {
                    this.form.disable();
                } else {
                    this.form.enable();
                    this.creatingNewVersion = false;
                }
            })
        );

        this.subscriptions.add(
            this.addVersionSrv.selectSavedVersion().subscribe(version => {
                if (version && version?.id !== null) {
                    this.projectsSrv.loadUploadedVersions();
                    this.router.navigate([`/projects/${this.project.id}/versions/${version.id}`]);
                }
            })
        );

        this.subscriptions.add(
            this.addVersionSrv.selectErrors().subscribe(errors => {
                this.errors = errors;
                this.modalUploadErrorText = 'There are some fields you need to review before sent the artwork.';
                if (errors.labels) {
                    this.creatingNewVersion = false;
                    this.modalView = this.VIEW_MODAL_UPLOAD_ERROR;
                } else if (errors.files) {
                    this.creatingNewVersion = false;
                    this.modalView = this.VIEW_MODAL_UPLOAD_ERROR;
                } else if (errors.status) {
                    this.creatingNewVersion = false;
                    this.modalView = this.VIEW_MODAL_UPLOAD_ERROR;
                } else if (errors.version) {
                    this.creatingNewVersion = false;
                    this.modalView = this.VIEW_MODAL_UPLOAD_ERROR;
                } else if (errors.forbidden || errors.unauthorized) {
                    this.creatingNewVersion = false;
                    this.modalUploadErrorText = 'Access denied: You do not have permission to upload this artwork.';
                    this.modalView = this.VIEW_MODAL_UPLOAD_ERROR;
                } else if (errors.server || errors.other) {
                    this.creatingNewVersion = false;
                    this.modalUploadErrorText = "You can not upload this artwork to this project. Please try again.<br/> If the error persists, please reach out to our support team through the Help Me button.";
                    this.modalView = this.VIEW_MODAL_UPLOAD_ERROR;
                }
            })
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
        this.addVersionSrv.clear();
        this.reset();
    }

    private initView() {

        if (this.allVersionStatus != null && (this.editTagsAccess  || (!this.editTagsAccess && this.availableTagGroups))) {

            this.status = this.STATUS_DONE; 
        }
    }


    isVersionReady(){
        // todo check files >0 according rule -> upload version with  no files????
        return (this.addVersionData.labels.length > 0 &&
                this.addVersionData.files.length > 0 && // REVIEW THIS
                this.addVersionData.version != null &&
                this.addVersionData.subversion != null &&
                this.addVersionData.status != null);
    }

    onSubmit(){
        if(!this.isVersionReady()){
            return
        }
        this.creatingNewVersion = true;
        this.form.disable();

        this.addVersionSrv.submitAddVersion(this.project.id, this.addVersionData);
    }

    onUploadError() {
        this.creatingNewVersion = false;
        this.modalView = this.VIEW_MODAL_NONE;
    }

    onCancel(){   
        // this.reset();
        this.location.back();
    }

    onSetStatus(status: number) {
        this.onAddVersionChange({status: status});
    }

    addChildForm(name: string, group: FormGroup) {
        this.form.addControl(name, group);
    }

    onAddVersionChange(changes: Partial<AddVersion>){
        this.addVersionData = { ...this.addVersionData, ...changes };
    }

    setFiles(files: Objects.FileMetadata[]){
        this.files = files;
        this.onAddVersionChange({files: files.map((f: Objects.FileMetadata) => {return f.uuid;})});
    }


    protected setLabels(labels: Array<Objects.Label>){
        // if no labels in links then take project labels
        if(this.project != null && labels.length == 0){
            labels = this.project.labels;
        } 
        this.versionLabels = Object.assign([], labels);
        this.onAddVersionChange({labels: labels.map((l: Objects.Label) => {return l.id;})});

        // When no edit access, find matching group and update version info
        if (!this.editTagsAccess && this.relatedVersionsGroups && this.relatedVersionsGroups.length > 0) {
            const matchingGroup = this.findMatchingGroup(labels);
            if (matchingGroup && matchingGroup.versions && matchingGroup.versions.length > 0) {
                const firstVersion = matchingGroup.versions[0];
                this.onAddVersionChange({
                    version: firstVersion.version_number,
                    subversion: firstVersion.subversion_number + 1 // Increment subversion for new version
                });
            }
        }
    }

    protected reset(){
        this.form.reset();
        this.addVersionData = this.addVersionDataDefault;
        this.addVersionData.status = +this.allVersionStatus[0].id;
        this.setLabels(this.labels);
        this.setFiles([]);
    }

    /**
     * Extract available tag groups from related versions.
     * Stores both the label arrays for display and full RelatedVersions objects for version info.
     * @param relatedsList List of related version groups
     */
    protected extractAvailableTagGroups(relatedsList: Array<Objects.RelatedVersions>): void {
        this.availableTagGroups = [];
        this.relatedVersionsGroups = [];
        
        if (!relatedsList || relatedsList.length === 0) {
            return;
        }

        // For each related version group, store both labels and full group object
        relatedsList.forEach((relatedGroup: Objects.RelatedVersions) => {
            if (relatedGroup.labels && relatedGroup.labels.length > 0) {
                this.availableTagGroups.push(relatedGroup.labels);
                this.relatedVersionsGroups.push(relatedGroup);
            }
        });
    }

    /**
     * Find the group that matches the given labels exactly
     * @param labels Labels to match against
     * @returns The matching RelatedVersions group or null
     */
    protected findMatchingGroup(labels: Array<Objects.Label>): Objects.RelatedVersions | null {
        if (!labels || labels.length === 0) {
            return null;
        }

        return this.relatedVersionsGroups.find((group: Objects.RelatedVersions) => {
            if (!group.labels || group.labels.length !== labels.length) {
                return false;
            }
            // Check if all labels match
            return labels.every((label: Objects.Label) =>
                group.labels.some((groupLabel: Objects.Label) => groupLabel.id === label.id)
            );
        }) || null;
    }

}